import { countMoreJobPreferences, hasValidJobSearchConstraints } from '../../../lib/job-search-preferences';
import { JobSearchConstraintsFields } from './JobSearchConstraintsFields';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Plus, Search, Briefcase, ChevronDown } from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useCVData, type CVJobPreferences } from '../../../lib/cv-mock-data';
import { captureSentryException } from '../../../lib/sentry';
import { useAISuggestJobTitles } from '../../../hooks/useOnboardingApi';
import { useCatalogRoles } from '../../../hooks/useTaxonomyApi';
import { searchCanonicalRoles } from '../../../lib/job-taxonomy';
import { WebSaveButton } from './web-form-elements';
import { useHydratedValueState } from './useHydratedFormState';
import { confirmDeleteCvItem } from './delete-confirmation';

interface EditJobPreferencesFormProps {
  onSave?: () => Promise<void> | void;
  onSaved?: () => void;
  closeOnSave?: boolean;
  ctaLabel?: string;
}

// The Suggested Job Titles list always aims to show this many chips.
const SUGGESTED_TITLE_TARGET = 5;

const LOCATION_TYPES = ['Remote', 'On-site', 'Hybrid'];
const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Freelance'];
const RELOCATION_OPTIONS = ['Open To Relocate', 'Not Open To Relocate'];
const RELOCATION_AREA_OPTIONS = ['Within UAE', 'Other GCC', 'Remote', 'Anywhere'];
const NOTICE_PERIOD_OPTIONS = ['1 Month', '3 Month', '6 Month', '1 Year'];

// Native's `theme.surfaceSecondary` maps to `--tabbio-surface-secondary` on
// web; `--surface-secondary` is the token the design uses. Fall back through
// the actual runtime var (set by ThemeProvider) so the fill always renders.
const SURFACE_SECONDARY =
  'var(--surface-secondary, var(--tabbio-surface-secondary, rgba(0,0,0,0.04)))';

function cloneJobPreferences(prefs: CVJobPreferences): CVJobPreferences {
  return {
    ...prefs,
    jobTitles: [...prefs.jobTitles],
    locationTypes: [...prefs.locationTypes],
    preferredLocations: [...prefs.preferredLocations],
    employmentTypes: [...prefs.employmentTypes],
    searchAreas: prefs.searchAreas ? [...prefs.searchAreas] : undefined,
    relocationAreas: prefs.relocationAreas ? [...prefs.relocationAreas] : undefined,
  };
}
function isEmptyJobPreferences(prefs: CVJobPreferences): boolean {
  return (
    !prefs.openToWork &&
    prefs.jobTitles.length === 0 &&
    prefs.locationTypes.length === 0 &&
    prefs.preferredLocations.length === 0 &&
    prefs.employmentTypes.length === 0 &&
    (prefs.searchAreas?.length ?? 0) === 0 &&
    !prefs.relocation &&
    (prefs.relocationAreas?.length ?? 0) === 0 &&
    !prefs.noticePeriod &&
    !(prefs.countries?.length || prefs.cities?.length || prefs.excludedRoles?.length || prefs.excludedCompanies?.length || prefs.seniorityLevels?.length) &&
    prefs.minimumSalary == null
  );
}

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--text-primary)',
  display: 'block',
  marginBottom: 8,
  fontFamily: 'inherit',
};

// A pill "suggestion" chip (border-radius 50, filled surface-secondary) with a
// trailing +: mirrors native's `filterChip`.
const suggestionChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 34,
  paddingInline: 16,
  borderRadius: 50,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  backgroundColor: SURFACE_SECONDARY,
  color: 'var(--text-primary)',
};

// ─── Open To Work card ──────────────────────────────────────────────
// Standalone neutral icon + bordered text/toggle control. The icon sits outside
// the control border so the row reads cleaner on both onboarding surfaces.
function OpenToWorkCard({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Briefcase size={21} color="var(--text-secondary)" strokeWidth={1.8} />
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          flex: 1,
          minHeight: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          width: '100%',
          paddingBlock: 12,
          paddingInlineStart: 16,
          paddingInlineEnd: 12,
          borderRadius: 16,
          border: '1px solid var(--input-border)',
          cursor: 'pointer',
          backgroundColor: hover ? 'rgba(0,0,0,0.04)' : 'var(--bg)',
          textAlign: 'start',
          fontFamily: 'inherit',
          transition: 'background-color 150ms',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: -0.14, color: 'var(--text-primary)' }}>
            {label}
          </div>
          <div style={{ fontSize: 12, letterSpacing: -0.12, color: 'var(--text-tertiary)', marginTop: 2 }}>
            {description}
          </div>
        </div>
        <div
          style={{
            width: 48,
            height: 30,
            borderRadius: 15,
            backgroundColor: value ? 'var(--text-primary)' : 'rgba(0,0,0,0.06)',
            border: value ? 'none' : '1px solid var(--input-border)',
            position: 'relative',
            transition: 'background-color 200ms, border-color 200ms',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: value ? 'var(--bg, var(--tabbio-bg, #ffffff))' : '#ffffff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              position: 'absolute',
              top: 3,
              insetInlineStart: value ? 21 : 3,
              transition: 'inset-inline-start 200ms',
            }}
          />
        </div>
      </button>
    </div>
  );
}

// Native `TogglePill` parity: gray-filled pill with a trailing + when
// unselected; solid purple with white text when selected. Used for every
// multi/single-select chip row (locations, employment, relocation, notice).
function TogglePillWeb({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      aria-pressed={selected}
      dir="auto"
      style={{
        ...suggestionChipStyle,
        backgroundColor: selected ? 'var(--color-discovery, #5e29ff)' : SURFACE_SECONDARY,
        color: selected ? '#ffffff' : 'var(--text-primary)',
      }}
    >
      {label}
      {!selected && <Plus size={14} strokeWidth={2} />}
    </button>
  );
}

// Chip row wrapper: flexWrap, matches the suggestion rows' spacing.
function PillRow({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>;
}

export function EditJobPreferencesForm({ onSave, onSaved, closeOnSave = true, ctaLabel }: EditJobPreferencesFormProps) {
  const { t } = useTranslation('seeker');
  const { profile, updateProfile } = useCVData();
  const router = useRouter();
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const [prefs, setPrefs] = useHydratedValueState(
    cloneJobPreferences(profile.jobPreferences),
    isEmptyJobPreferences,
  );
  const [newTitle, setNewTitle] = useState('');
  const [showMore, setShowMore] = useState(false);
  const moreSetCount = countMoreJobPreferences(prefs);

  const toggle = (field: 'locationTypes' | 'employmentTypes' | 'relocationAreas', value: string) => {
    setPrefs((p) => {
      const list = p[field] ?? [];
      return {
        ...p,
        [field]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
      };
    });
  };

  // Relocation + notice period are single-select (tap active to clear).
  const selectSingle = (field: 'relocation' | 'noticePeriod', value: string) => {
    setPrefs((p) => ({ ...p, [field]: p[field] === value ? undefined : value }));
  };

  const [aiTitles, setAiTitles] = useState<string[]>([]);
  const aiSuggest = useAISuggestJobTitles();
  const aiRequestedRef = useRef(false);

  // Fetch AI-suggested job titles once (when the profile has something to go
  // on) to seed the Suggested Job Titles list. Silent on failure: users can enter their own preferred titles.
  useEffect(() => {
    if (aiRequestedRef.current) return;
    const hasSignal =
      !!profile.headline?.trim() ||
      profile.experience.length > 0 ||
      profile.skillGroups.some((group) => group.items.length > 0);
    if (!hasSignal) return;
    aiRequestedRef.current = true;
    aiSuggest.mutate(
      {
        currentTitle: profile.headline || undefined,
        experience: profile.experience.map((exp) => ({
          title: exp.role,
          company: exp.company,
          description: exp.overview || undefined,
          achievements: exp.bullets || [],
          current: /present/i.test(exp.period || ''),
        })),
        topSkills: profile.skillGroups.flatMap((group) => group.items).slice(0, 12),
        existingTitles: prefs.jobTitles,
      },
      {
        onSuccess: (result) => {
          if (result.titles?.length) setAiTitles(result.titles);
        },
      },
    );
  }, [aiSuggest, profile, prefs.jobTitles]);

  // AI-suggested titles, filtered to
  // drop anything already selected or duplicated.
  const suggestedTitles = useMemo(() => {
    const selectedLower = new Set(prefs.jobTitles.map((title) => title.toLowerCase()));
    const seen = new Set<string>();
    const result: string[] = [];
    const add = (title: string) => {
      const text = title.trim();
      const key = text.toLowerCase();
      if (!text || selectedLower.has(key) || seen.has(key)) return;
      seen.add(key);
      result.push(text);
    };
    aiTitles.forEach(add);
    return result.slice(0, SUGGESTED_TITLE_TARGET);
  }, [aiTitles, prefs.jobTitles]);

  // ─── Live role search ────────────────────────────────────────────────
  // The search box drives a debounced lookup against the canonical role
  // catalog (`/api/taxonomy/roles`, which itself falls back to the bundled
  // `searchCanonicalRoles` offline). We defer to the local dataset whenever
  // the server yields nothing (e.g. unseeded catalog) so search always works.
  const trimmedTitle = newTitle.trim();
  const [debouncedTitle, setDebouncedTitle] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedTitle(trimmedTitle), 200);
    return () => clearTimeout(id);
  }, [trimmedTitle]);

  const isSearching = debouncedTitle.length >= 2;
  const rolesQuery = useCatalogRoles(debouncedTitle, 12, isSearching);

  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const server = rolesQuery.data ?? [];
    const source = server.length > 0 ? server : searchCanonicalRoles(debouncedTitle, 12);
    const selectedLower = new Set(prefs.jobTitles.map((title) => title.toLowerCase()));
    const seen = new Set<string>();
    const results: string[] = [];
    // Show each suggestion in the script the user is actually typing in: not
    // the app locale. The catalog matches queries against both languages, so an
    // Arabic-locale user typing an English role would otherwise see (and add)
    // the Arabic label swapped in for what they typed. Prefer the label that
    // matches the query's script, falling back to the other. (Issue #125)
    const queryIsArabic = /[\u0600-\u06FF]/.test(debouncedTitle);
    for (const option of source) {
      const preferred = queryIsArabic ? option.labelAr : option.label;
      const fallback = queryIsArabic ? option.label : option.labelAr;
      const label = preferred?.trim() || fallback?.trim();
      const key = label?.toLowerCase();
      if (!label || !key || selectedLower.has(key) || seen.has(key)) continue;
      seen.add(key);
      results.push(label);
      if (results.length >= 10) break;
    }
    return results;
  }, [isSearching, rolesQuery.data, debouncedTitle, prefs.jobTitles]);

  // Offer the raw typed text as an add when it doesn't already match a result
  // or an existing chip: lets users add roles the catalog doesn't know.
  const canAddCustom = useMemo(() => {
    if (!trimmedTitle) return false;
    const lower = trimmedTitle.toLowerCase();
    const known = new Set([
      ...prefs.jobTitles.map((title) => title.toLowerCase()),
      ...searchResults.map((title) => title.toLowerCase()),
    ]);
    return !known.has(lower);
  }, [trimmedTitle, prefs.jobTitles, searchResults]);

  // Add without clearing the query so several matches can be picked in a row.
  const addTitle = (title: string) => {
    const text = title.trim();
    if (!text) return;
    setPrefs((current) =>
      current.jobTitles.some((existing) => existing.toLowerCase() === text.toLowerCase())
        ? current
        : { ...current, jobTitles: [...current.jobTitles, text] },
    );
  };

  // Add + clear the input (Enter / the "Add custom" chip). Mirrors native.
  const addChip = (field: 'jobTitles', value: string, setter: (v: string) => void) => {
    const text = value.trim();
    if (!text) return;
    setPrefs((current) => ({ ...current, [field]: [...current[field], text] }));
    setter('');
  };

  const removeChip = (field: 'jobTitles', index: number) => {
    void confirmDeleteCvItem(t, 'jobTitle', () => {
      setPrefs((current) => ({
        ...current,
        [field]: current[field].filter((_, currentIndex) => currentIndex !== index),
      }));
    });
  };

  const handleSave = async () => {
    if (!hasValidJobSearchConstraints(prefs)) {
      // The invalid fields (salary, cities, exclusions) live in the collapsed
      // section; open it so the error points at something visible.
      setShowMore(true);
      toast.error(t('jobMatching.searchValidationError'));
      return;
    }
    if (isSaving) return;
    const previousPrefs = profile.jobPreferences;
    updateProfile({ jobPreferences: prefs });
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave();
        toast.success(t('cv.edit.saved'));
        if (onSaved) {
          onSaved();
        } else if (closeOnSave && router.canGoBack()) {
          router.back();
        }
      } catch (err) {
        updateProfile({ jobPreferences: previousPrefs });
        captureSentryException(err, { scope: 'edit_job_preferences_save_web' });
        toast.error(t('cv.edit.saveFailed'));
      } finally {
        setIsSaving(false);
      }
    } else {
      router.back();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Open To Work card */}
      <OpenToWorkCard
        label={t('cv.edit.openToWorkToggle')}
        description={t('cv.edit.openToWorkDesc')}
        value={prefs.openToWork}
        onChange={(v) => setPrefs((p) => ({ ...p, openToWork: v }))}
      />

      {/* Search + selected chips + suggestions (grouped, tight spacing) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Search bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            height: 52,
            paddingInline: 20,
            borderRadius: 40,
            backgroundColor: SURFACE_SECONDARY,
          }}
        >
          <Search size={20} color="var(--text-tertiary)" strokeWidth={1.5} style={{ flexShrink: 0 }} />
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t('cv.edit.searchJobTitle')}
            dir="auto"
            aria-label={t('cv.edit.searchJobTitle')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addChip('jobTitles', newTitle, setNewTitle);
              }
            }}
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--text-primary)',
              fontSize: 14,
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Selected job title chips */}
        {prefs.jobTitles.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {prefs.jobTitles.map((title, index) => (
              <span
                key={index}
                dir="auto"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  paddingInline: 12,
                  paddingBlock: 6,
                  borderRadius: 9999,
                  backgroundColor: 'var(--tabbio-button-soft-bg)',
                  color: 'var(--tabbio-button-soft-text)',
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: 'inherit',
                }}
              >
                {title}
                <button
                  type="button"
                  onClick={() => removeChip('jobTitles', index)}
                  aria-label={`Remove ${title}`}
                  style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
                >
                  <X size={12} strokeWidth={2} />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Live role search results */}
        {isSearching && (searchResults.length > 0 || canAddCustom) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ ...sectionLabelStyle, marginBottom: 0 }}>
                {t('cv.edit.suggestedRoles')}
              </span>
              {rolesQuery.isFetching && (
                <span
                  aria-hidden
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    border: '2px solid var(--input-border)',
                    borderTopColor: 'var(--text-tertiary)',
                    animation: 'tabbio-spinner-spin 0.7s linear infinite',
                    display: 'inline-block',
                  }}
                />
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {searchResults.map((title) => (
                <button
                  key={title}
                  type="button"
                  onClick={() => addTitle(title)}
                  aria-label={title}
                  dir="auto"
                  style={suggestionChipStyle}
                >
                  {title}
                  <Plus size={14} strokeWidth={2} />
                </button>
              ))}
              {canAddCustom && (
                <button
                  type="button"
                  onClick={() => addChip('jobTitles', newTitle, setNewTitle)}
                  aria-label={t('cv.edit.addCustomRole', { query: trimmedTitle })}
                  dir="auto"
                  style={{
                    ...suggestionChipStyle,
                    backgroundColor: 'var(--tabbio-button-soft-bg)',
                    color: 'var(--tabbio-button-soft-text)',
                  }}
                >
                  {t('cv.edit.addCustomRole', { query: trimmedTitle })}
                  <Plus size={14} strokeWidth={2} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Suggested job titles (AI-seeded, topped up with placeholders) */}
        {!isSearching && suggestedTitles.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ ...sectionLabelStyle, marginBottom: 0 }}>
              {t('cv.edit.jobTitles')}
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {suggestedTitles.map((title) => (
                <button
                  key={title}
                  type="button"
                  onClick={() => addTitle(title)}
                  aria-label={title}
                  dir="auto"
                  style={suggestionChipStyle}
                >
                  {title}
                  <Plus size={14} strokeWidth={2} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <JobSearchConstraintsFields value={prefs} onChange={setPrefs} part="countries" />

      {/* Work arrangement */}
      <div>
        <label style={sectionLabelStyle}>
          {t('jobMatching.workArrangement')}
        </label>
        <PillRow>
          {LOCATION_TYPES.map((option) => (
            <TogglePillWeb
              key={option}
              label={option}
              selected={prefs.locationTypes.includes(option)}
              onPress={() => toggle('locationTypes', option)}
            />
          ))}
        </PillRow>
      </div>

      {/* Employment Types */}
      <div>
        <label style={sectionLabelStyle}>
          {t('cv.edit.employmentTypes')}
        </label>
        <PillRow>
          {EMPLOYMENT_TYPES.map((option) => (
            <TogglePillWeb
              key={option}
              label={option}
              selected={prefs.employmentTypes.includes(option)}
              onPress={() => toggle('employmentTypes', option)}
            />
          ))}
        </PillRow>
      </div>

      {/* Everything that only narrows the search further. Collapsed by default
          so the form reads as a handful of choices; values set in here are
          still saved, and the row says how many are set. */}
      <div style={{ borderTop: '1px solid var(--input-border)', paddingTop: 4 }}>
        <button
          type="button"
          onClick={() => setShowMore((open) => !open)}
          aria-expanded={showMore}
          aria-controls={showMore ? 'job-preferences-more' : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            paddingBlock: 12,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            textAlign: 'start',
            fontFamily: 'inherit',
            color: 'var(--text-primary)',
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 16, fontWeight: 600 }}>
              {t('cv.edit.morePreferences')}
              {moreSetCount > 0 ? (
                <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>
                  {' · '}
                  {t('cv.edit.morePreferencesSet', { n: moreSetCount })}
                </span>
              ) : null}
            </span>
            <span style={{ display: 'block', fontSize: 13, color: 'var(--text-tertiary)', marginTop: 2 }}>
              {t('cv.edit.morePreferencesHint')}
            </span>
          </span>
          <ChevronDown
            size={20}
            strokeWidth={1.75}
            color="var(--text-secondary)"
            style={{
              flexShrink: 0,
              transform: showMore ? 'rotate(180deg)' : undefined,
              transition: 'transform 200ms',
            }}
          />
        </button>

        {showMore && (
          <div
            id="job-preferences-more"
            style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingTop: 8 }}
          >
            <JobSearchConstraintsFields value={prefs} onChange={setPrefs} part="filters" />

            {/* Relocation */}
            <div>
              <label style={sectionLabelStyle}>
                {t('cv.edit.relocation')}
              </label>
              <PillRow>
                {RELOCATION_OPTIONS.map((option) => (
                  <TogglePillWeb
                    key={option}
                    label={option}
                    selected={prefs.relocation === option}
                    onPress={() => selectSingle('relocation', option)}
                  />
                ))}
              </PillRow>
            </div>

            {/* Where To? */}
            <div>
              <label style={sectionLabelStyle}>
                {t('cv.edit.whereTo')}
              </label>
              <PillRow>
                {RELOCATION_AREA_OPTIONS.map((option) => (
                  <TogglePillWeb
                    key={option}
                    label={option}
                    selected={(prefs.relocationAreas ?? []).includes(option)}
                    onPress={() => toggle('relocationAreas', option)}
                  />
                ))}
              </PillRow>
            </div>

            {/* Notice Period */}
            <div>
              <label style={sectionLabelStyle}>
                {t('cv.edit.noticePeriod')}
              </label>
              <PillRow>
                {NOTICE_PERIOD_OPTIONS.map((option) => (
                  <TogglePillWeb
                    key={option}
                    label={option}
                    selected={prefs.noticePeriod === option}
                    onPress={() => selectSingle('noticePeriod', option)}
                  />
                ))}
              </PillRow>
            </div>
          </div>
        )}
      </div>

      <WebSaveButton label={ctaLabel ?? t('cv.edit.save')} onClick={handleSave} loading={isSaving} disabled={isSaving} />
    </div>
  );
}
